import { useEffect, useState } from "react";
import { api, formatApiError, fmtMoney } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Calculator, Sparkles, RefreshCw } from "lucide-react";
import { toast } from "sonner";

function Row({ label, value, isDeduction, isBold, currency }) {
  return (
    <div className={`flex items-center justify-between py-2 border-b border-neutral-100 last:border-0 ${isBold ? "border-t-2 border-neutral-950" : ""}`}>
      <span className={`text-sm font-mono ${isBold ? "font-semibold text-neutral-950" : "text-neutral-600"}`}>{label}</span>
      <span className={`font-mono ${isBold ? "text-base font-semibold" : "text-sm"} ${isDeduction ? "text-[#FF3B30]" : "text-neutral-950"}`}>
        {isDeduction ? "− " : ""}{fmtMoney(value, currency)}
      </span>
    </div>
  );
}

const defaults = {
  basic: 30000, da: 3000, hra: 12000, allowances: 5000,
  state: "maharashtra", enable_pf: true, enable_esi: true, other_deductions: 0,
};

export default function CalculatorPage() {
  const { user } = useAuth();
  const [states, setStates] = useState([]);
  const [form, setForm] = useState(defaults);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const currency = "INR"; // India calculator uses INR

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/calculator/states");
        setStates(data);
      } catch (e) { console.error("calculator/states failed", e); }
    })();
    calculate(defaults);
    // eslint-disable-next-line
  }, []);

  const calculate = async (payload) => {
    setLoading(true);
    try {
      const { data } = await api.post("/calculator/india", payload);
      setResult(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Calc failed"));
    } finally {
      setLoading(false);
    }
  };

  const set = (k, coerce = (x) => x) => (e) => {
    const v = coerce(e.target.type === "checkbox" ? e.target.checked : e.target.value);
    const next = { ...form, [k]: v };
    setForm(next);
    calculate(next);
  };

  return (
    <div>
      <PageHeader
        testid="page-calculator"
        eyebrow="Statutory · India"
        title="Salary calculator."
        description="Enter Basic, DA, HRA and allowances. PF, ESI, Professional Tax, LWF and TDS (New Regime FY 2025-26 with 87A rebate) are computed instantly."
        actions={
          <button onClick={() => { setForm(defaults); calculate(defaults); }} data-testid="btn-reset-calc"
            className="inline-flex items-center gap-2 border border-neutral-300 px-4 py-2.5 font-mono text-sm hover:bg-neutral-950 hover:text-white transition-colors">
            <RefreshCw className="w-4 h-4" /> Reset sample
          </button>
        }
      />

      <div className="p-8 grid lg:grid-cols-5 gap-8">
        {/* Input side */}
        <div className="lg:col-span-2 border border-neutral-200 bg-white p-6 space-y-5" data-testid="calc-inputs">
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#002FA7] flex items-center gap-2">
            <Calculator className="w-3.5 h-3.5" /> Inputs (monthly ₹)
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Basic</label>
              <input type="number" min="0" value={form.basic} onChange={set("basic", Number)} data-testid="calc-basic"
                className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#002FA7] outline-none font-mono text-sm" />
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">DA</label>
              <input type="number" min="0" value={form.da} onChange={set("da", Number)} data-testid="calc-da"
                className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#002FA7] outline-none font-mono text-sm" />
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">HRA</label>
              <input type="number" min="0" value={form.hra} onChange={set("hra", Number)} data-testid="calc-hra"
                className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#002FA7] outline-none font-mono text-sm" />
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Other allowances</label>
              <input type="number" min="0" value={form.allowances} onChange={set("allowances", Number)} data-testid="calc-allowances"
                className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#002FA7] outline-none font-mono text-sm" />
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">State (for PT & LWF)</label>
            <select value={form.state} onChange={set("state")} data-testid="calc-state"
              className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#002FA7] outline-none font-mono text-sm bg-white">
              <option value="">— none —</option>
              {states.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.state} {s.pt_supported ? "· PT" : ""}{s.lwf_supported ? " · LWF" : ""}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Other deductions (₹)</label>
            <input type="number" min="0" value={form.other_deductions} onChange={set("other_deductions", Number)} data-testid="calc-other"
              className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#002FA7] outline-none font-mono text-sm" />
          </div>

          <div className="pt-4 border-t border-neutral-200 space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={form.enable_pf} onChange={set("enable_pf")} data-testid="calc-pf" />
              <span className="text-sm font-mono">Deduct EPF (12% of Basic + DA)</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={form.enable_esi} onChange={set("enable_esi")} data-testid="calc-esi" />
              <span className="text-sm font-mono">Deduct ESI (0.75% if gross ≤ ₹21,000)</span>
            </label>
          </div>
        </div>

        {/* Output side */}
        <div className="lg:col-span-3 space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="border border-neutral-200 bg-white p-5">
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-500">Gross</div>
              <div className="mt-2 font-display font-black text-3xl tracking-tighter">{result ? fmtMoney(result.gross, currency) : "—"}</div>
            </div>
            <div className="border border-neutral-200 bg-white p-5">
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#FF3B30]">Deductions</div>
              <div className="mt-2 font-display font-black text-3xl tracking-tighter">{result ? fmtMoney(result.total_deductions, currency) : "—"}</div>
            </div>
            <div className="border border-neutral-950 bg-neutral-950 text-white p-5">
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#FF3B30]">Net · take-home</div>
              <div className="mt-2 font-display font-black text-3xl tracking-tighter">{result ? fmtMoney(result.net, currency) : "—"}</div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="border border-neutral-200 bg-white p-6" data-testid="calc-earnings">
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#002FA7] mb-4">Earnings</div>
              {result && (
                <>
                  <Row label="Basic" value={result.basic} currency={currency} />
                  <Row label="Dearness Allowance (DA)" value={result.da} currency={currency} />
                  <Row label="HRA" value={result.hra} currency={currency} />
                  <Row label="Other allowances" value={result.allowances} currency={currency} />
                  <Row label="Gross earnings" value={result.gross} isBold currency={currency} />
                </>
              )}
            </div>

            <div className="border border-neutral-200 bg-white p-6" data-testid="calc-deductions">
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#FF3B30] mb-4">Statutory deductions</div>
              {result && (
                <>
                  <Row label="EPF (12% of Basic + DA)" value={result.pf} isDeduction currency={currency} />
                  <Row label="ESI (0.75% employee share)" value={result.esi} isDeduction currency={currency} />
                  <Row label={`Professional Tax${result.state ? ` · ${result.state}` : ""}`} value={result.pt} isDeduction currency={currency} />
                  <Row label="Labour Welfare Fund (LWF)" value={result.lwf} isDeduction currency={currency} />
                  <Row label="TDS · monthly (New Regime FY25-26)" value={result.tds} isDeduction currency={currency} />
                  <Row label="Other deductions" value={result.other_deductions} isDeduction currency={currency} />
                  <Row label="Total deductions" value={result.total_deductions} isDeduction isBold currency={currency} />
                </>
              )}
            </div>
          </div>

          <div className="border border-neutral-200 bg-[#FAFAFA] p-5 flex items-start gap-3">
            <Sparkles className="w-4 h-4 text-[#002FA7] mt-0.5 shrink-0" />
            <div className="text-xs text-neutral-600 leading-relaxed font-mono">
              <strong className="text-neutral-950">Rules applied · FY 2025-26.</strong> EPF: 12% of (Basic + DA). ESI: 0.75% employee share, applicable only if gross ≤ ₹21,000. Professional Tax by state slab. LWF monthly average by state. TDS: New Regime with ₹75,000 standard deduction, 87A rebate up to ₹12 lakh taxable, plus 4% health &amp; education cess. This is a monthly calculator — annual TDS is divided by 12.
            </div>
          </div>

          {loading && <div className="text-xs font-mono text-neutral-500">Recalculating…</div>}
        </div>
      </div>
    </div>
  );
}
